import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth, SignIn } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { resolveInvite, HttpError } from "../api/invites";
import { invitePreviewQueryOptions } from "../api/queries";
import { createOrgSlug } from "../lib/slug";

export const Route = createFileRoute("/invite")({
  validateSearch: (s) => ({
    code: String(s["code"] ?? ""),
  }),
  component: InviteAccept,
});

function InviteAccept() {
  const { isSignedIn, getToken } = useAuth();
  const { code } = Route.useSearch();
  const navigate = useNavigate();

  const [resolveError, setResolveError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const previewQuery = useQuery(invitePreviewQueryOptions(getToken, code));

  if (!isSignedIn) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <SignIn redirectUrl={window.location.href} />
      </div>
    );
  }

  if (!code) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">Invalid invitation link.</p>
          <p className="mt-2 text-sm text-gray-500">
            The link you followed appears to be malformed.
          </p>
        </div>
      </div>
    );
  }

  async function handleResolve(accept: boolean) {
    setResolveError(null);
    setIsPending(true);
    try {
      await resolveInvite(getToken, code, accept);
      if (accept && previewQuery.data) {
        const { organizationId, organizationName } = previewQuery.data;
        navigate({
          to: "/organization/$slug/overview",
          params: { slug: createOrgSlug(organizationId, organizationName) },
        });
      } else {
        navigate({ to: "/" });
      }
    } catch {
      setResolveError("Something went wrong. Please try again.");
    } finally {
      setIsPending(false);
    }
  }

  function renderContent() {
    if (previewQuery.isPending) {
      return (
        <>
          <div className="h-6 w-3/4 animate-pulse rounded bg-gray-200" />
          <div className="mt-2 h-4 w-1/2 animate-pulse rounded bg-gray-200" />
          <div className="mt-6 flex gap-3">
            <div className="h-9 flex-1 animate-pulse rounded-lg bg-gray-200" />
            <div className="h-9 flex-1 animate-pulse rounded-lg bg-gray-200" />
          </div>
        </>
      );
    }

    if (previewQuery.isError) {
      const err = previewQuery.error;
      let message = "Something went wrong. Please try again later.";
      if (err instanceof HttpError) {
        if (err.status === 404) {
          message = "This invitation link is invalid.";
        } else if (err.status === 410) {
          message = "This invitation has expired or been revoked.";
        }
      }
      return (
        <p className="text-sm text-gray-700">{message}</p>
      );
    }

    const preview = previewQuery.data;
    return (
      <>
        <h1 className="text-xl font-semibold text-gray-900">
          You've been invited to join {preview.organizationName}
        </h1>
        {preview.positionName && (
          <p className="mt-1 text-sm text-gray-500">as {preview.positionName}</p>
        )}

        {resolveError && (
          <p className="mt-4 text-sm text-red-600">{resolveError}</p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={() => handleResolve(true)}
            disabled={isPending}
            className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-darker disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isPending ? "Accepting…" : "Accept"}
          </button>
          <button
            onClick={() => handleResolve(false)}
            disabled={isPending}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Decline
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
        {renderContent()}
      </div>
    </div>
  );
}
